import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ClassName, twx } from '@/utils/tailwind';

export const categoryPinSizes = ['xs', 'sm', 'base', 'lg'] as const;
export type CategoryPinSize = (typeof categoryPinSizes)[number];

interface SizeSpec {
  box: string;
  iconPx: number;
}

const sizeSpecs: Record<CategoryPinSize, SizeSpec> = {
  xs: { box: 'w-5 h-5', iconPx: 9 },
  sm: { box: 'w-6 h-6', iconPx: 10 },
  base: { box: 'w-8 h-8', iconPx: 12 },
  lg: { box: 'w-10 h-10', iconPx: 16 },
};

type FAIconStyle = React.CSSProperties & { [key: `--fa-${string}`]: string | undefined };

export interface CategoryPinProps {
  /** Background color — usually `categoryDefinitions[c].pinColor`. */
  color: string;
  icon: IconDefinition;
  size?: CategoryPinSize;
  /** Use solid icon weight (duotone collapsed to a single color) instead of
   * the default semi-transparent secondary layer. Reads cleaner in timeline
   * rows and map markers where the icon is small. */
  solid?: boolean;
  /** Drops the white ring + shadow when the pin sits inside an already-
   * structured container (e.g. a hover row) and doesn't need extra
   * separation. */
  flat?: boolean;
  ariaLabel?: string;
  className?: ClassName;
}

/**
 * Colored circular badge with an icon inside. The atomic "this place is in
 * category X" element — appears on the map (heart-shaped variant lives in
 * ItineraryMap as a special case), in trip-place rows, in the scouted list,
 * and inside the trip-selector list when a place is selected.
 */
export const CategoryPin = ({
  color,
  icon,
  size = 'sm',
  solid = false,
  flat = false,
  ariaLabel,
  className,
}: CategoryPinProps) => {
  const spec = sizeSpecs[size];
  // aria-label requires an accessible role; we expose `img` when labelled
  // (the pin then announces as a graphic), and stay role-less + label-less
  // when decorative (the sibling text already names what this represents).
  return (
    <span
      {...(ariaLabel ? { role: 'img', 'aria-label': ariaLabel } : {})}
      className={twx(
        'shrink-0 rounded-full flex items-center justify-center',
        spec.box,
        !flat && 'border-2 border-white shadow-sm',
        className,
      )}
      style={{ backgroundColor: color }}
    >
      <FontAwesomeIcon
        icon={icon}
        style={
          {
            width: spec.iconPx,
            height: spec.iconPx,
            '--fa-primary-color': 'white',
            '--fa-secondary-color': 'white',
            '--fa-secondary-opacity': solid ? '1' : '0.8',
          } as FAIconStyle
        }
      />
    </span>
  );
};
